import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiArrowLeft,
  FiDownload,
  FiInstagram,
  FiShare2,
  FiShuffle,
  FiDroplet,
  FiSquare,
  FiEyeOff,
  FiPlus,
} from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { shareNode, downloadNode, nodeToPngBlob } from "./shareCanvas";
import ShareCard, {
  GPS_CARD_ORDER,
  ordemDasArtes,
  buildMealShareData,
  buildWorkoutShareData,
  buildAchievementShareData,
} from "./ShareCard";
import { lerPrefs, salvarPrefs, embaralhar } from "./sharePrefs";
import StudioPanel from "./StudioPanel";
import { montarDadosGps } from "../../../atividade/arteGps";
import { estudioApi, useModoEstudio } from "./shareStudio";
import ShareRewardModal from "../../../compartilhamento/ShareRewardModal";
import { COMPARTILHAMENTO } from "../../../../config/planoConfig";
import Style from "../../../../styles/features/nutricao/ShareRefeicao.module.css";

// Interruptor da barra de opções. Estado à vista (LED + preenchimento), não só
// no rótulo — quem abre a tela precisa saber o que está ligado sem ler.
const Toggle = ({ ligado, onToggle, icone, rotulo }) => (
  <button
    type="button"
    className={`${Style.opcaoToggle} ${ligado ? Style.opcaoToggleOn : ""}`}
    onClick={onToggle}
    aria-pressed={ligado}
  >
    <span className={Style.opcaoLed} aria-hidden="true" />
    {icone}
    <span>{rotulo}</span>
  </button>
);

// Destrava o ESTÚDIO das artes. Gesto escondido de propósito: quem compartilha
// refeição não pode esbarrar num editor. Segurar 1,2s no título ou na barra de
// interruptores abre; o X do painel fecha. (No desktop também dá pelo console:
// `lesvitaShareStudio.on()`.)
const MS_TOQUE_LONGO = 1200;

const useToqueLongo = (aoDisparar) => {
  const timer = useRef(null);
  const cancelar = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => cancelar, []);
  return {
    onPointerDown: () => {
      cancelar();
      timer.current = setTimeout(() => {
        timer.current = null;
        aoDisparar();
      }, MS_TOQUE_LONGO);
    },
    onPointerUp: cancelar,
    onPointerLeave: cancelar,
    onPointerCancel: cancelar,
    onContextMenu: (evento) => evento.preventDefault(),
  };
};

const ShareRefeicao = ({
  type = "meal",
  capturedImage,
  apiResult,
  translatedName,
  workout,
  profileData,
  analysisId,
  /* Conquista: {tipo: 'STREAK'|'VITORIA'|'MARCO', dias, melhor, posicao,
     pontos, titulo, imageUrl}. Ver buildAchievementShareData. */
  conquista,
  /* Atividade gravada por GPS (documento de ATIVIDADES). Com traçado, as
     artes do desenho "Compartilhar Atividade" (1t/1u) entram NA FRENTE das de
     treino; `workout` continua sendo o registro de treino dela, que alimenta
     o resto do carrossel. Sem traçado, nada muda. */
  atividadeGps,
  onBack,
  showHeader = true,
  onSelectFeed,
}) => {
  /* Três origens, dois conjuntos de arte: refeição usa 1a…1i; treino e
     CONQUISTA usam 1k…1s, porque as duas têm a mesma forma (título + até
     quatro métricas + foto). Ver `buildAchievementShareData`. */
  const ehConquista = type === "achievement";
  const isWorkout = type === "workout" || ehConquista;

  // Preferências por aparelho (localStorage) — ver sharePrefs.js.
  const [prefs, setPrefs] = useState(lerPrefs);
  const alternar = (chave) => () =>
    setPrefs((atual) => {
      const novo = { ...atual, [chave]: !atual[chave] };
      salvarPrefs(novo);
      return novo;
    });

  /* Atividade GPS: a métrica extra do chip (no máximo uma — o desenho fecha
     em quatro métricas) e o corte das pontas. `null` quando a atividade não
     tem traçado: aí a tela é a de treino de sempre. */
  const [extraGps, setExtraGps] = useState(null);
  const dadosGps = useMemo(
    () =>
      isWorkout && !ehConquista && atividadeGps
        ? montarDadosGps(atividadeGps, {
            ocultarPontas: prefs.ocultarPontas,
            extra: extraGps,
          })
        : null,
    [isWorkout, ehConquista, atividadeGps, prefs.ocultarPontas, extraGps]
  );
  const ehGps = Boolean(dadosGps);

  const data = useMemo(() => {
    if (ehConquista) return buildAchievementShareData(conquista, profileData);
    if (isWorkout) {
      const base = buildWorkoutShareData(workout, profileData);
      return dadosGps ? { ...base, gps: dadosGps } : base;
    }
    return buildMealShareData({ capturedImage, apiResult, translatedName });
  }, [
    ehConquista,
    conquista,
    isWorkout,
    workout,
    profileData,
    capturedImage,
    apiResult,
    translatedName,
    dadosGps,
  ]);

  /* FORMATO: story (9:16) ou feed (4:5). Não é a mesma arte esticada — cada
     formato tem a própria lista de artes portadas e os próprios ajustes de
     pixel, porque a altura útil do 4:5 é ~30% menor e todo `dy` medido no
     9:16 deixa de valer. */
  const feedOnly = !!onSelectFeed;
  const transparente = !feedOnly && prefs.transparente;
  const formato = feedOnly || prefs.formatoFeed ? "FEED" : "STORY";
  const ordemBase = ordemDasArtes(type, formato);
  // Embaralha uma vez por MONTAGEM da tela (dependência é só a flag e a ordem
  // base): re-sortear a cada render trocaria as artes debaixo do dedo de quem
  // está deslizando o carrossel.
  /* As artes GPS ficam SEMPRE na frente — inclusive com "Ordem aleatória",
     que só embaralha o resto: a primeira arte de uma atividade gravada é a
     do traçado. Sem fundo, a `1u` (marca) viraria cópia da `1t` (as duas
     caem no sticker), então sai. */
  const ordemGps = useMemo(
    () =>
      ehGps && !feedOnly
        ? transparente
          ? GPS_CARD_ORDER.slice(0, 1)
          : GPS_CARD_ORDER
        : [],
    [ehGps, feedOnly, transparente]
  );
  const ordemResto = useMemo(
    () =>
      feedOnly
        ? [...ordemBase, "photo"]
        : prefs.ordemAleatoria
        ? embaralhar(ordemBase)
        : ordemBase,
    [feedOnly, prefs.ordemAleatoria, ordemBase]
  );
  const cardOrder = useMemo(
    () => [...ordemGps, ...ordemResto],
    [ordemGps, ordemResto]
  );

  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Estúdio: modo de edição das artes (shareStudio.js). Só o cartão em foco
  // responde ao dedo, e o painel precisa do NÓ dele para listar os elementos —
  // daí o ref virar estado quando a arte em foco muda.
  const modoEstudio = useModoEstudio();
  const swiperRef = useRef(null);
  const [noAtivo, setNoAtivo] = useState(null);
  const destravarEstudio = useToqueLongo(() => estudioApi.ligar());

  useEffect(() => {
    setNoAtivo(cardRefs.current[activeIndex] || null);
  }, [activeIndex, cardOrder, prefs.transparente, modoEstudio]);

  const [isProcessing, setIsProcessing] = useState(false);
  // Recompensa: abre depois que a folha de compartilhamento do sistema fecha
  // SEM erro. Quem confirma o post é a pessoa, no botão do modal — a Web Share
  // API resolve igual quando o compartilhamento é cancelado.
  const [showReward, setShowReward] = useState(false);
  // Pista de para ONDE foi (activityType da folha nativa: pacote no Android,
  // UTI no iOS). É só uma DICA para pré-selecionar o canal no modal — quem
  // decide é a pessoa. Nunca vira o valor autoritativo.
  const [canalHint, setCanalHint] = useState("");

  const activeCard = () => cardRefs.current[activeIndex];
  const sufixo = prefs.transparente ? "-transparente" : "";
  const fileName = () =>
    `lesvita-${ehGps ? "atividade" : isWorkout ? "treino" : "refeicao"}-${
      cardOrder[activeIndex]
    }${sufixo}`;
  const shareMeta = isWorkout
    ? { title: "Treino LesVita", text: "Confira meu treino no app LesVita!" }
    : {
        title: "Refeição LesVita",
        text: "Confira a análise da minha refeição no app LesVita!",
      };

  const processCard = async (action) => {
    const node = activeCard();
    if (!node || isProcessing) return;
    setIsProcessing(true);
    try {
      if (action === "feed") {
        const blob = await nodeToPngBlob(node);
        if (!blob) throw new Error("Falha ao gerar imagem.");
        await onSelectFeed(
          new File([blob], "refeicao-feed.png", { type: "image/png" })
        );
      } else if (action === "download")
        await downloadNode(node, fileName(), shareMeta);
      else {
        const resultado = await shareNode(node, fileName(), shareMeta);
        setCanalHint(resultado?.activityType || "");
        setShowReward(true);
      }
    } catch (error) {
      if (error?.name !== "AbortError" && error?.message !== "Share canceled") {
        console.error(
          `Erro ao ${
            action === "download" ? "baixar" : "compartilhar"
          } imagem:`,
          error
        );
        alert(
          `Não foi possível ${
            action === "download" ? "baixar" : "compartilhar"
          } a imagem. Tente novamente.`
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  /* ── Ajuste ao espaço ─────────────────────────────────────────────────────
     O card tem tamanho fixo (270×480 story, 270×337.5 feed) e o rodapé
     precisa caber SEMPRE. Mede o que sobrou para o carrossel e reduz o
     preview (`--share-fit`) até caber, em vez de deixar o botão para fora da
     tela. ResizeObserver: teclado, rotação e o cabeçalho do offcanvas mudam
     a altura disponível sem remontar. */
  const carouselRef = useRef(null);
  const screenRef = useRef(null);
  useEffect(() => {
    const area = carouselRef.current;
    const screen = screenRef.current;
    if (!area || !screen || typeof ResizeObserver === "undefined") return undefined;
    const PADDING_SWIPER = 44; // 12 em cima + 32 dos pontinhos
    const alturaCard = formato === "FEED" ? 337.5 : 480;
    const ajustar = () => {
      const atual =
        parseFloat(screen.style.getPropertyValue("--share-fit")) || 1;
      // Só encolhe quando o carrossel realmente não cabe na área (pai com
      // altura limitada). Num pai sem limite a área cresce com o conteúdo,
      // nunca transborda, e a tela rola com o rodapé sticky — encolher ali
      // realimentaria a própria medida e o card minguaria a cada rodada.
      const transborda = area.scrollHeight > area.clientHeight + 1;
      if (!transborda && atual >= 1) return;
      const disponivel = area.clientHeight - PADDING_SWIPER;
      const fit = Math.max(0.45, Math.min(1, disponivel / alturaCard));
      if (Math.abs(fit - atual) < 0.005) return;
      screen.style.setProperty("--share-fit", fit.toFixed(3));
      swiperRef.current?.update?.();
    };
    ajustar();
    const ro = new ResizeObserver(ajustar);
    ro.observe(area);
    return () => ro.disconnect();
  }, [formato]);

  return (
    <section
      ref={screenRef}
      className={`${Style.shareScreen} ${
        showHeader ? Style.fullScreen : Style.embeddedScreen
      }`}
      aria-label={`Compartilhar ${
        ehGps ? "atividade" : isWorkout ? "treino" : "refeição"
      }`}
      data-formato={formato}
      data-estudio-aberto={modoEstudio ? "1" : undefined}
    >
      {showHeader && (
        <header className={Style.screenHeader}>
          {onBack ? (
            <button
              type="button"
              className={Style.backButton}
              onClick={onBack}
              aria-label="Voltar"
            >
              <FiArrowLeft />
            </button>
          ) : (
            <span className={Style.headerSpacer} />
          )}
          <h2 {...destravarEstudio}>
            Compartilhar{" "}
            {ehConquista
              ? "conquista"
              : ehGps
              ? "atividade"
              : isWorkout
              ? "treino"
              : "refeição"}
          </h2>
          <span className={Style.headerSpacer} />
        </header>
      )}

      {!feedOnly && (
        <div className={Style.opcoesRow} {...destravarEstudio}>
          <Toggle
            ligado={prefs.ordemAleatoria}
            onToggle={alternar("ordemAleatoria")}
            icone={<FiShuffle aria-hidden="true" />}
            rotulo="Ordem aleatória"
          />
          <Toggle
            ligado={prefs.transparente}
            onToggle={alternar("transparente")}
            icone={<FiDroplet aria-hidden="true" />}
            rotulo="Sem fundo"
          />
          <Toggle
            ligado={prefs.formatoFeed}
            onToggle={alternar("formatoFeed")}
            icone={<FiSquare aria-hidden="true" />}
            rotulo="Formato feed"
          />
        </div>
      )}

      {/* Só com atividade GPS: a métrica extra das artes 1t/1u (uma por vez,
          entre as que a atividade tem) e a privacidade das pontas. */}
      {!feedOnly && ehGps && (
        <div className={`${Style.opcoesRow} ${Style.opcoesRowGps}`}>
          {dadosGps.extrasDisponiveis.map((extra) => (
            <Toggle
              key={extra.chave}
              ligado={extraGps === extra.chave}
              onToggle={() =>
                setExtraGps((atual) =>
                  atual === extra.chave ? null : extra.chave
                )
              }
              icone={<FiPlus aria-hidden="true" />}
              rotulo={extra.rotulo}
            />
          ))}
          <Toggle
            ligado={prefs.ocultarPontas}
            onToggle={alternar("ocultarPontas")}
            icone={<FiEyeOff aria-hidden="true" />}
            rotulo="Ocultar início e fim"
          />
        </div>
      )}

      <div className={Style.carouselArea} ref={carouselRef}>
        <Swiper
          modules={[Pagination]}
          slidesPerView="auto"
          centeredSlides
          spaceBetween={12}
          pagination={{ clickable: true, dynamicBullets: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          // No estúdio o dedo no cartão ARRASTA ELEMENTO; trocar de arte passa a
          // ser pelas setas do painel, senão os dois gestos brigam.
          allowTouchMove={!modoEstudio}
          className={Style.shareSwiper}
        >
          {cardOrder.map((variant, index) => (
            <SwiperSlide key={variant} className={Style.shareSlide}>
              <div
                className={`${Style.cardPreview} ${
                  transparente ? Style.cardPreviewXadrez : ""
                }`}
                data-estudio-guias={
                  modoEstudio && index === activeIndex ? "1" : undefined
                }
              >
                <ShareCard
                  variant={variant}
                  data={data}
                  formato={formato}
                  transparent={transparente}
                  editavel={!feedOnly && index === activeIndex}
                  ref={(node) => {
                    cardRefs.current[index] = node;
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className={Style.shareFooter}>
        {!feedOnly && (
          <p className={Style.instagramHint}>
            <FiInstagram aria-hidden="true" />
            <span>
              Marque <strong>@lesvita.fit</strong> e ganhe{" "}
              <strong>+{COMPARTILHAMENTO.XP} XP</strong> —{" "}
              {COMPARTILHAMENTO.MULTIPLICADOR_VERIFICADO}× se verificarmos
            </span>
          </p>
        )}
        <div className={Style.actionRow}>
          {!feedOnly && (
            <button
              type="button"
              className={Style.downloadButton}
              onClick={() => processCard("download")}
              disabled={isProcessing}
              aria-label="Baixar imagem"
            >
              <FiDownload />
            </button>
          )}
          <button
            type="button"
            className={Style.shareButton}
            onClick={() => processCard(feedOnly ? "feed" : "share")}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FiShare2 />
            )}
            <span>
              {isProcessing
                ? "Preparando…"
                : feedOnly
                ? "Usar esta foto no feed"
                : "Compartilhar"}
            </span>
          </button>
        </div>
      </div>

      {modoEstudio && !feedOnly && (
        <StudioPanel
          variante={cardOrder[activeIndex]}
          raiz={noAtivo}
          indice={activeIndex}
          total={cardOrder.length}
          onArte={(delta) => {
            if (delta > 0) swiperRef.current?.slideNext();
            else swiperRef.current?.slidePrev();
          }}
          onBaixar={() => processCard("download")}
          baixando={isProcessing}
        />
      )}

      <ShareRewardModal
        show={showReward}
        type={type}
        origemId={(() => {
          /* A origem identifica o que foi compartilhado na fila de
             verificação. Conquista não tem registro: usa o tipo + o número,
             que é o que distingue "30 dias" de "100 dias" no painel. */
          if (ehConquista) {
            return `${conquista?.tipo || "CONQUISTA"}_${
              conquista?.dias || conquista?.posicao || ""
            }`;
          }
          return isWorkout
            ? workout?.id || workout?.ID || ""
            : analysisId || "";
        })()}
        canalHint={canalHint}
        onClose={() => setShowReward(false)}
      />
    </section>
  );
};

export default ShareRefeicao;
